import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiX } from "react-icons/fi";
import Button from "../../../../../components/ui/Button";
import InputField from "../../../../../components/ui/InputField";
import { motion, AnimatePresence } from "framer-motion";
import { Currency, CreateCurrencyInput, UpdateCurrencyInput } from "../../../../../types/currency";

interface CurrencyFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCurrencyInput | UpdateCurrencyInput) => Promise<void>;
  initialData?: Currency;
  isLoading?: boolean;
  branchId: string;
}

const CurrencyFormModal: React.FC<CurrencyFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isLoading = false,
  branchId,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CreateCurrencyInput>({
    branch_id: branchId,
    name: "",
    iso: "",
    symbol: "",
    exchange_rate: null,
    is_base: false,
    precision: 2,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const isEditing = !!initialData;

  useEffect(() => {
    if (initialData) {
      setFormData({
        branch_id: initialData.branch_id,
        name: initialData.name,
        iso: initialData.iso,
        symbol: initialData.symbol || "",
        exchange_rate: initialData.exchange_rate,
        is_base: initialData.is_base || false,
        precision: initialData.precision ?? 2,
      });
    } else {
      setFormData({
        branch_id: branchId,
        name: "",
        iso: "",
        symbol: "",
        exchange_rate: null,
        is_base: false,
        precision: 2,
      });
    }
    setErrors({});
  }, [initialData, isOpen, branchId]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('currencies.currencyName') + " " + t('common.actions');
    }

    if (!formData.iso.trim()) {
      newErrors.iso = t('currencies.currencyCode') + " " + t('common.actions');
    } else if (formData.iso.trim().length !== 3) {
      newErrors.iso = "ISO code must be 3 characters (e.g., USD, EUR)";
    }

    if (formData.symbol && formData.symbol.trim().length > 5) {
      newErrors.symbol = "Symbol must be 5 characters or less";
    }

    if (
      formData.exchange_rate !== null &&
      (isNaN(Number(formData.exchange_rate)) || Number(formData.exchange_rate) < 0)
    ) {
      newErrors.exchange_rate = "Exchange rate must be a positive number";
    }

    return newErrors;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else if (type === "number") {
      setFormData((prev) => ({
        ...prev,
        [name]: value === "" ? null : Number(value),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/25 backdrop-blur-sm"
              onClick={onClose}
            />

            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {isEditing ? t('currencies.editCurrency') : t('currencies.addCurrency')}
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer"
                >
                  <FiX size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Currency Name */}
                <InputField
                  label={t('currencies.currencyName')}
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., US Dollar"
                  error={errors.name}
                />

                {/* ISO Code */}
                <InputField
                  label={t('currencies.currencyCode')}
                  name="iso"
                  type="text"
                  value={formData.iso}
                  onChange={handleInputChange}
                  placeholder="e.g., USD, EUR, AED"
                  maxLength={3}
                  error={errors.iso}
                />

                {/* Symbol */}
                <InputField
                  label={t('currencies.currencySymbol')}
                  name="symbol"
                  type="text"
                  value={formData.symbol || ""}
                  onChange={handleInputChange}
                  placeholder="e.g., $, €, ₽"
                  error={errors.symbol}
                />

                {/* Exchange Rate */}
                <InputField
                  label={t('currencies.exchangeRate')}
                  name="exchange_rate"
                  type="number"
                  value={formData.exchange_rate ?? ""}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  error={errors.exchange_rate}
                />

                {/* Precision */}
                <InputField
                  label={t('currencies.precision')}
                  name="precision"
                  type="number"
                  value={formData.precision ?? 2}
                  onChange={handleInputChange}
                  placeholder="2"
                  min={0}
                  max={6}
                  error={errors.precision}
                />

                {/* Is Base Currency */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_base"
                    name="is_base"
                    checked={formData.is_base || false}
                    onChange={handleInputChange}
                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                  />
                  <label
                    htmlFor="is_base"
                    className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                  >
                    {t('currencies.setAsBase')}
                  </label>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isLoading}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    {isEditing ? t('common.edit') : t('common.save')}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CurrencyFormModal;
